import { useKineticMap, useKineticMapLiveProjection } from 'components';
import { LanePathProps, LanePathSegmentProps } from './types';

const arrowSize = 3;
const strokeWidth = 8;

const LaneSegment: React.FunctionComponent<LanePathSegmentProps> = ({
  coordinates,
  point,
  color,
  disabled,
  lighten,
  onClick,
  onMouseOut,
  onMouseOver,
  arrowEnd,
  arrowStart,
  arrowMarkerId,
  segmentIndex,
  title,
}) => {
  const point1 = useKineticMapLiveProjection([...coordinates]);
  const point2 = useKineticMapLiveProjection([...point]);
  const pointerEvents = !disabled && !!(onClick || onMouseOver || onMouseOut || title);
  const { mapRef } = useKineticMap();

  if (!point1 || !point2) {
    return null;
  }

  const opacity = typeof lighten === 'number' ? lighten : 0.7;

  return (
    <line
      markerEnd={arrowEnd ? `url(#${arrowMarkerId})` : undefined}
      markerStart={arrowStart ? `url(#${arrowMarkerId})` : undefined}
      onClick={(e) => {
        const {
          nativeEvent: { offsetX, offsetY },
        } = e;
        const { lat, lng } = mapRef.current.unproject([offsetX, offsetY]);
        onClick?.(e, segmentIndex ?? 0, { lat, lng });
      }}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      pointerEvents={pointerEvents ? undefined : 'none'}
      stroke={color}
      data-segment-index={segmentIndex}
      strokeOpacity={lighten ? opacity : 1.0}
      strokeWidth={strokeWidth}
      style={pointerEvents ? { cursor: 'pointer', pointerEvents: 'all' } : undefined}
      x1={point1.x}
      y1={point1.y}
      x2={point2.x}
      y2={point2.y}
    >
      {!!title && <title>{title}</title>}
    </line>
  );
};

const LanePath: React.FunctionComponent<LanePathProps> = (props) => {
  const {
    arrowEnd,
    arrowStart,
    color,
    coordinates,
    disabled,
    id,
    lighten,
    onClick,
    onMouseOut,
    onMouseOver,
    title,
  } = props;

  const pointerEvents = !disabled && !!(onClick || onMouseOver || onMouseOut || title);
  const hasArrow = arrowEnd || arrowStart;

  let arrowMarkerId = '';
  if (hasArrow) {
    arrowMarkerId = `${id}-${color}-arrow`;
  }

  const opacity = typeof lighten === 'number' ? lighten : 0.7;

  return (
    <>
      {hasArrow && (
        <defs>
          <marker
            id={arrowMarkerId}
            markerWidth={arrowSize / 2}
            markerHeight={arrowSize}
            orient='auto'
            pointerEvents={pointerEvents ? undefined : 'none'}
            refX={arrowStart ? arrowSize / 2 - 0.1 : 0.1}
            refY={arrowSize / 2}
          >
            <path
              d={`M${arrowStart ? arrowSize / 2 : 0},0 V${arrowSize} L${
                arrowStart ? 0 : arrowSize / 2
              },${arrowSize / 2} Z`}
              fill={color}
              fillOpacity={lighten ? opacity : 1.0}
            />
          </marker>
        </defs>
      )}
      {/* render a line segment for each set of points in the coordinates array */}
      {/* we're slicing then using the i as `currentIndex - 1`  */}
      {coordinates.slice(1).map((point, i) => {
        const lineProps = {
          ...props,
          coordinates: coordinates[i],
        };
        return (
          <LaneSegment
            segmentIndex={i}
            arrowMarkerId={arrowMarkerId}
            {...lineProps}
            point={point}
            key={i}
          />
        );
      })}
    </>
  );
};

export default LanePath;
